import React, { useState } from "react";
import { api, toCSV, money, num, fmtDate } from "../lib/api";
import { PageHeader, Card, Button, Field, Input } from "../components/shared";
import { toast } from "sonner";
import { FileBarChart, Download, Printer } from "lucide-react";
import { ITEM_TYPES } from "./Cutting";

const REPORTS = [
  { id: "fabric-purchase", name: "Fabric Purchase Report" },
  { id: "cutting", name: "Cutting Report" },
  { id: "jobber-issue-receipt", name: "All Jobber Issue & Receipt Report" },
  { id: "cleared-slips", name: "Cleared Slips Report" },
  { id: "goods-dispatched", name: "Goods Dispatched Report" },
];

const CS = "cleared-slips";
const CS_COLS = [
  ["cutting_sheet_number", "Cutting Sheet No"], ["date", "Clearing Slip Date"], ["slip_number", "Slip No"], ["design_number", "Design"],
  ["item_type", "Item"], ["fresh_qty", "Fresh Qty"], ["mix_qty", "Mix Qty"], ["reject_qty", "Reject Qty"], ["remarks", "Remarks"],
];
const CS_NUM = ["fresh_qty", "mix_qty", "reject_qty"];
const DJC = "design-jobber-cost";
const ISR = "jobber-issue-receipt";
const ISR_COLS = [
  ["date", "Date"], ["type", "Type"], ["challan_number", "Challan"], ["jobber_name", "Jobber"],
  ["design_number", "Design"], ["process", "Process"], ["issued_qty", "Issued"],
  ["received_qty", "Received"], ["rejected_qty", "Rejected"], ["short_qty", "Short"],
  ["rate", "Rate/Pc"], ["amount", "Amount"],
];

export default function Reports() {
  const [active, setActive] = useState(null);
  const [rows, setRows] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [djc, setDjc] = useState([]);
  const [djcF, setDjcF] = useState({ design: "", jobber: "", process: "" });

  const djcParams = () => ({
    from_date: from || undefined, to_date: to || undefined,
    design: djcF.design || undefined, jobber: djcF.jobber || undefined, process: djcF.process || undefined,
  });

  const loadDJC = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${DJC}`, { params: djcParams() });
      setDjc(data);
    } catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };

  const loadISR = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${ISR}`, { params: djcParams() });
      setRows(data);
    } catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };

  const [csF, setCsF] = useState({ design: "", item: "" });
  const loadCS = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${CS}`, { params: { from_date: from || undefined, to_date: to || undefined, design: csF.design || undefined, item: csF.item || undefined } });
      setRows(data);
    } catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };

  const run = async (rep) => {
    setActive(rep);
    if (rep.id === CS) { await loadCS(); return; }
    if (rep.id === DJC) { await loadDJC(); return; }
    if (rep.id === ISR) { await loadISR(); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${rep.id}`);
      let list = Array.isArray(data) ? data : Object.entries(data).flatMap(([k, v]) => (v || []).map((x) => ({ process: k, ...x })));
      if (from) list = list.filter((r) => (r.created_at || r.date || r.order_date || "") >= from);
      if (to) list = list.filter((r) => (r.created_at || r.date || r.order_date || "") <= to + "T23:59");
      setRows(list);
    } catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };

  const exportDJCExcel = async () => {
    try {
      const res = await api.get(`/reports/${DJC}/export`, { params: djcParams(), responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u; a.download = "Design_Jobber_Cost.xlsx"; a.click();
      URL.revokeObjectURL(u);
    } catch { toast.error("Excel export failed"); }
  };

  const printDJC = () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup blocked — allow popups to print"); return; }
    const body = djc.map((g) => `
      <h3>Design ${g.design_number} <small>${[g.colour, g.item_type].filter(Boolean).join(" · ")}</small></h3>
      <table>
        <thead><tr><th>Jobber</th><th>Process</th><th class="r">Qty</th><th class="r">Rate/Pc</th><th class="r">Amount</th></tr></thead>
        <tbody>${g.lines.map((l) => `<tr><td>${l.jobber_name}</td><td>${l.process}</td><td class="r">${num(l.qty)}</td><td class="r">₹${num(l.rate)}</td><td class="r">₹${num(l.amount)}</td></tr>`).join("")}</tbody>
        <tfoot>
          <tr><td colspan="2">Total Production Qty</td><td class="r">${num(g.total_qty)}</td><td>Total Jobber Cost</td><td class="r">₹${num(g.total_cost)}</td></tr>
          <tr><td colspan="4">Cost Per Piece</td><td class="r">₹${num(g.cost_per_piece)}</td></tr>
        </tfoot>
      </table>`).join("");
    w.document.write(`<html><head><title>Design-wise Jobber Cost Report</title><style>
      body{font-family:Inter,Arial,sans-serif;padding:24px;color:#0f172a}
      h1{font-size:20px;margin:0 0 12px}h3{margin:20px 0 6px}small{font-weight:400;color:#64748b}
      table{width:100%;border-collapse:collapse;margin-bottom:8px;font-size:13px}
      th,td{border:1px solid #cbd5e1;padding:6px 8px;text-align:left}.r{text-align:right}
      thead th{background:#1e40af;color:#fff}tfoot td{font-weight:700;background:#f1f5f9}
    </style></head><body><h1>Design-wise Jobber Cost Report</h1>${body || "<p>No data</p>"}</body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  const columns = rows.length ? Object.keys(rows[0]).filter((k) => typeof rows[0][k] !== "object" && !["id", "created_by", "updated_at"].includes(k)).slice(0, active?.id === "fabric-purchase" ? 13 : 10) : [];
  const isDJC = active?.id === DJC;
  const isISR = active?.id === ISR;
  const isCS = active?.id === CS;
  const csTotal = (k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);

  return (
    <div>
      <PageHeader title="Reports" subtitle="20+ ERP reports with filters, Excel/CSV export & print" />
      <div className="grid lg:grid-cols-4 gap-4">
        <Card className="p-3 lg:col-span-1 h-fit">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Available Reports</p>
          <div className="space-y-1 max-h-[600px] overflow-y-auto">
            {REPORTS.map((r) => (
              <button key={r.id} onClick={() => run(r)} data-testid={`report-${r.id}`}
                className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 ${active?.id === r.id ? "bg-blue-800 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                <FileBarChart size={14} /> {r.name}
              </button>
            ))}
          </div>
        </Card>
        <Card className="p-4 lg:col-span-3">
          {!active ? (
            <div className="h-64 grid place-items-center text-slate-400 text-sm">Select a report to view</div>
          ) : (
            <>
              <div className="flex flex-wrap items-end justify-between gap-2 mb-4">
                <div>
                  <p className="text-lg font-semibold text-slate-800 font-head">{active.name}</p>
                  <p className="text-xs text-slate-400">{isDJC ? `${djc.length} designs` : `${rows.length} records`}</p>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  {isDJC && (
                    <>
                      <div><label className="block text-xs text-slate-500 mb-1">Design</label><input value={djcF.design} onChange={(e) => setDjcF({ ...djcF, design: e.target.value })} placeholder="D-1001" className="border border-slate-300 rounded px-2 py-1.5 text-sm w-28" data-testid="djc-design" /></div>
                      <div><label className="block text-xs text-slate-500 mb-1">Jobber</label><input value={djcF.jobber} onChange={(e) => setDjcF({ ...djcF, jobber: e.target.value })} placeholder="Jobber" className="border border-slate-300 rounded px-2 py-1.5 text-sm w-28" data-testid="djc-jobber" /></div>
                      <div><label className="block text-xs text-slate-500 mb-1">Process</label><input value={djcF.process} onChange={(e) => setDjcF({ ...djcF, process: e.target.value })} placeholder="Process" className="border border-slate-300 rounded px-2 py-1.5 text-sm w-28" data-testid="djc-process" /></div>
                    </>
                  )}
                  {isCS && (
                    <>
                      <div><label className="block text-xs text-slate-500 mb-1">Design</label><input value={csF.design} onChange={(e) => setCsF({ ...csF, design: e.target.value })} placeholder="Design" className="border border-slate-300 rounded px-2 py-1.5 text-sm w-28" data-testid="cs-design" /></div>
                      <div><label className="block text-xs text-slate-500 mb-1">Item</label>
                        <select value={csF.item} onChange={(e) => setCsF({ ...csF, item: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm w-32" data-testid="cs-item">
                          <option value="">All items</option>{ITEM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select></div>
                    </>
                  )}
                  {isISR && (
                    <>
                      <div><label className="block text-xs text-slate-500 mb-1">Jobber</label><input value={djcF.jobber} onChange={(e) => setDjcF({ ...djcF, jobber: e.target.value })} placeholder="Jobber" className="border border-slate-300 rounded px-2 py-1.5 text-sm w-28" data-testid="isr-jobber" /></div>
                      <div><label className="block text-xs text-slate-500 mb-1">Design</label><input value={djcF.design} onChange={(e) => setDjcF({ ...djcF, design: e.target.value })} placeholder="Design" className="border border-slate-300 rounded px-2 py-1.5 text-sm w-28" data-testid="isr-design" /></div>
                      <div><label className="block text-xs text-slate-500 mb-1">Process</label><input value={djcF.process} onChange={(e) => setDjcF({ ...djcF, process: e.target.value })} placeholder="Process" className="border border-slate-300 rounded px-2 py-1.5 text-sm w-28" data-testid="isr-process" /></div>
                    </>
                  )}
                  <div><label className="block text-xs text-slate-500 mb-1">From</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border border-slate-300 rounded px-2 py-1.5 text-sm" /></div>
                  <div><label className="block text-xs text-slate-500 mb-1">To</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border border-slate-300 rounded px-2 py-1.5 text-sm" /></div>
                  <Button variant="secondary" size="sm" onClick={() => run(active)}>Apply</Button>
                  {isDJC ? (
                    <>
                      <Button size="sm" onClick={exportDJCExcel} data-testid="djc-export-excel"><Download size={14} /> Excel</Button>
                      <Button size="sm" variant="secondary" onClick={printDJC} data-testid="djc-print"><Printer size={14} /> Print / PDF</Button>
                    </>
                  ) : (
                    <Button size="sm" onClick={() => toCSV(isCS ? rows.map((r) => Object.fromEntries(CS_COLS.map(([k, l]) => [l, r[k]]))) : rows, `${active.id}.csv`)} data-testid="report-export"><Download size={14} /> Export</Button>
                  )}
                </div>
              </div>

              {loading ? <div className="h-40 grid place-items-center"><div className="h-8 w-8 border-4 border-blue-800 border-t-transparent rounded-full animate-spin" /></div> : isCS ? (
                <div className="border rounded-lg overflow-x-auto max-h-[560px]" data-testid="cs-panel">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0"><tr>{CS_COLS.map(([k, label]) => <th key={k} className={`px-3 py-2 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap ${CS_NUM.includes(k) ? "text-right" : "text-left"}`}>{label}</th>)}</tr></thead>
                    <tbody>{rows.map((r, i) => (
                      <tr key={i} className="border-t hover:bg-slate-50" data-testid={`cs-row-${i}`}>
                        {CS_COLS.map(([k]) => (
                          <td key={k} className={`px-3 py-1.5 whitespace-nowrap text-slate-700 ${CS_NUM.includes(k) ? "text-right tabular" : ""}`}>
                            {k === "date" ? fmtDate(r[k]) : CS_NUM.includes(k) ? num(r[k]) : (r[k] || "—")}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {!rows.length && <tr><td colSpan={CS_COLS.length} className="text-center py-10 text-slate-400">No cleared slips for the selected filters</td></tr>}
                    </tbody>
                    {rows.length > 0 && (
                      <tfoot><tr className="border-t-2 bg-slate-50 font-semibold" data-testid="cs-totals">
                        {CS_COLS.map(([k], i) => <td key={k} className={`px-3 py-2 ${CS_NUM.includes(k) ? "text-right tabular" : ""}`}>{i === 0 ? "TOTAL" : CS_NUM.includes(k) ? num(csTotal(k)) : ""}</td>)}
                      </tr></tfoot>
                    )}
                  </table>
                </div>
              ) : isISR ? (
                <div className="border rounded-lg overflow-x-auto max-h-[560px]" data-testid="isr-panel">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0"><tr>{ISR_COLS.map(([k, label]) => <th key={k} className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">{label}</th>)}</tr></thead>
                    <tbody>{rows.map((r, i) => (
                      <tr key={i} className="border-t hover:bg-slate-50" data-testid={`isr-row-${i}`}>
                        {ISR_COLS.map(([k]) => (
                          <td key={k} className="px-3 py-1.5 whitespace-nowrap text-slate-700">
                            {k === "date" ? fmtDate(r[k])
                              : k === "type" ? <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${r[k] === "Issue" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{r[k]}</span>
                              : k === "amount" || k === "rate" ? (r[k] !== "" && r[k] != null ? money(r[k]) : "—")
                              : (r[k] !== "" && r[k] != null ? (["issued_qty", "received_qty", "rejected_qty", "short_qty"].includes(k) ? num(r[k]) : String(r[k])) : "—")}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {!rows.length && <tr><td colSpan={ISR_COLS.length} className="text-center py-10 text-slate-400">No issue / receipt records for the selected filters</td></tr>}
                    </tbody>
                  </table>
                </div>
              ) : isDJC ? (
                <div className="space-y-4 max-h-[560px] overflow-y-auto" data-testid="djc-panel">
                  {djc.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No jobber cost data for the selected filters</div>}
                  {djc.map((g) => (
                    <div key={g.design_number} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`djc-design-${g.design_number}`}>
                      <div className="bg-slate-50 px-4 py-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-200">
                        <div>
                          <p className="font-semibold text-slate-800">Design {g.design_number}</p>
                          <p className="text-xs text-slate-500">{[g.colour, g.item_type].filter(Boolean).join(" · ")} · Total Qty {num(g.total_qty)} pcs</p>
                        </div>
                        <div className="flex gap-5 text-right">
                          <div><p className="text-xs text-slate-500">Total Jobber Cost</p><p className="font-bold text-blue-800">{money(g.total_cost)}</p></div>
                          <div><p className="text-xs text-slate-500">Cost / Piece</p><p className="font-bold text-emerald-700">{money(g.cost_per_piece)}</p></div>
                        </div>
                      </div>
                      <table className="w-full text-sm">
                        <thead className="bg-white text-xs uppercase text-slate-500 border-b"><tr>
                          <th className="px-4 py-2 text-left">Jobber</th><th className="px-4 py-2 text-left">Process</th>
                          <th className="px-4 py-2 text-right">Qty</th><th className="px-4 py-2 text-right">Rate/Pc</th>
                          <th className="px-4 py-2 text-right">Amount</th><th className="px-4 py-2 text-left">Challan</th><th className="px-4 py-2 text-left">Date</th>
                        </tr></thead>
                        <tbody>{g.lines.map((l, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-4 py-1.5 font-medium text-slate-700">{l.jobber_name}</td>
                            <td className="px-4 py-1.5">{l.process}</td>
                            <td className="px-4 py-1.5 text-right tabular">{num(l.qty)}</td>
                            <td className="px-4 py-1.5 text-right tabular">{money(l.rate)}</td>
                            <td className="px-4 py-1.5 text-right tabular font-medium">{money(l.amount)}</td>
                            <td className="px-4 py-1.5 text-slate-500">{l.challan_number || "-"}</td>
                            <td className="px-4 py-1.5 text-slate-500">{fmtDate(l.date)}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border rounded-lg overflow-x-auto max-h-[520px]">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0"><tr>{columns.map((c) => <th key={c} className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">{c.replace(/_/g, " ")}</th>)}</tr></thead>
                    <tbody>{rows.map((r, i) => (
                      <tr key={i} className="border-t hover:bg-slate-50">{columns.map((c) => (
                        <td key={c} className="px-3 py-1.5 whitespace-nowrap text-slate-700">{typeof r[c] === "boolean" ? (r[c] ? "Yes" : "No") : String(r[c] ?? "-").slice(0, 40)}</td>
                      ))}</tr>
                    ))}
                    {!rows.length && <tr><td colSpan={columns.length || 1} className="text-center py-10 text-slate-400">No data</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
